import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Check, BarChart3, Eye, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { BottomSheet } from "./BottomSheet";

// Normalize legacy single-question polls into the multi-question shape.
function normalize(poll) {
  if (!poll) return null;
  if (poll.questions) return poll;
  if (poll.options) {
    return {
      questions: [{ id: "q0", type: "mc", text: poll.question || "Poll", options: poll.options }],
      responses: poll.votes ? Object.fromEntries(Object.entries(poll.votes).map(([u, v]) => [u, { q0: v }])) : {},
      creator_id: poll.creator_id,
    };
  }
  return null;
}

export function Poll({ poll: rawPoll, kind, id, userId, testidPrefix = "poll", collapsed = false }) {
  const poll = normalize(rawPoll);
  const [responses, setResponses] = useState(poll?.responses || {});
  const [draft, setDraft] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { setResponses(rawPoll?.responses || (rawPoll?.votes ? normalize(rawPoll).responses : {}) || {}); }, [rawPoll]);

  if (!poll) return null;
  const myResp = responses[userId];
  const answered = !!myResp;
  const isCreator = poll.creator_id === userId;

  const setDraftFor = (qid, val) => setDraft((d) => ({ ...d, [qid]: val }));

  const submit = async () => {
    // validate sum questions total 100
    for (const q of poll.questions) {
      if (q.type === "sum") {
        const map = draft[q.id] || {};
        const tot = Object.values(map).reduce((a, b) => a + (Number(b) || 0), 0);
        if (tot !== 100) { toast.error(`"${q.text}" must total exactly 100 points (currently ${tot}).`); return; }
      }
      if (q.type === "ranking" && !draft[q.id]) setDraftFor(q.id, q.options.map((_, i) => i));
    }
    setSubmitting(true);
    try {
      const payload = { ...draft };
      poll.questions.forEach((q) => { if (q.type === "ranking" && !payload[q.id]) payload[q.id] = q.options.map((_, i) => i); });
      const { data } = await api.post("/polls/respond", { kind, id, responses: payload });
      setResponses(data.responses);
      toast.success("Vote recorded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const openResults = async () => {
    try {
      const { data } = await api.get(`/polls/results?kind=${kind}&id=${id}`);
      setResults(data); setShowResults(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resultsPortal = createPortal(
    <BottomSheet open={showResults} onClose={() => setShowResults(false)} title="Poll Results" height="94dvh">
      <ResultsView results={results} />
    </BottomSheet>,
    document.body
  );

  const pollBody = (
    <div className={collapsed ? "" : "mt-3 rounded-xl p-3"} style={collapsed ? {} : { background: "var(--overlay)", border: "1px solid var(--border)" }} data-testid={collapsed ? `${testidPrefix}-body` : testidPrefix}>
      <div className="flex items-center gap-1.5 mb-2">
        <BarChart3 size={14} className="gold-text" />
        <span className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>Poll · {poll.questions.length} question{poll.questions.length > 1 ? "s" : ""}</span>
      </div>

      <div className="space-y-4">
        {poll.questions.map((q, qi) => (
          <QuestionBlock key={q.id} q={q} qi={qi} answered={answered} myAnswer={myResp?.[q.id]}
            draft={draft[q.id]} setDraft={(v) => setDraftFor(q.id, v)} responses={responses} testidPrefix={testidPrefix} />
        ))}
      </div>

      {!answered && (
        <motion.button whileTap={{ scale: 0.97 }} onClick={submit} disabled={submitting} data-testid={`${testidPrefix}-submit`}
          className="w-full mt-3 rounded-lg py-2.5 text-sm font-semibold flex items-center justify-center gap-2" style={{ background: "var(--accent)", color: "#fff" }}>
          {submitting ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Submit vote
        </motion.button>
      )}

      {isCreator && (
        <button onClick={openResults} data-testid={`${testidPrefix}-see-results`}
          className="w-full mt-2 rounded-lg py-2 text-xs font-semibold flex items-center justify-center gap-1.5" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
          <Eye size={13} /> See results (creator only)
        </button>
      )}
    </div>
  );

  if (collapsed) {
    return (
      <>
        <button onClick={() => setOpen(true)} data-testid={`${testidPrefix}-take`}
          className="w-full mt-3 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2"
          style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)", border: "1px solid var(--accent)", color: "var(--accent)" }}>
          <BarChart3 size={15} /> {answered ? "View poll" : "Take Poll"} · {poll.questions.length} question{poll.questions.length > 1 ? "s" : ""}
        </button>
        {createPortal(
          <BottomSheet open={open} onClose={() => setOpen(false)} title="Poll" height="94dvh">
            {pollBody}
          </BottomSheet>,
          document.body
        )}
        {resultsPortal}
      </>
    );
  }

  return (
    <>
      {pollBody}
      {resultsPortal}
    </>
  );
}

function QuestionBlock({ q, qi, answered, myAnswer, draft, setDraft, responses, testidPrefix }) {
  const tid = `${testidPrefix}-q${qi}`;
  const voters = Object.values(responses).filter((r) => r && r[q.id] !== undefined);

  if (q.type === "short") {
    return (
      <div data-testid={tid}>
        <div className="text-sm font-medium mb-1.5">{q.text}</div>
        {answered ? (
          <div className="text-sm rounded-lg px-3 py-2" style={{ background: "var(--bg-surface)", color: "var(--text-secondary)" }}>{myAnswer || "—"}</div>
        ) : (
          <input value={draft || ""} onChange={(e) => setDraft(e.target.value)} placeholder="Your answer…" data-testid={`${tid}-input`}
            className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
        )}
      </div>
    );
  }

  if (q.type === "mc") {
    const counts = q.options.map((_, oi) => voters.filter((r) => r[q.id] === oi).length);
    const total = counts.reduce((a, b) => a + b, 0) || 1;
    return (
      <div data-testid={tid}>
        <div className="text-sm font-medium mb-1.5">{q.text}</div>
        <div className="space-y-1.5">
          {q.options.map((o, oi) => {
            const pct = Math.round((counts[oi] / total) * 100);
            const mine = myAnswer === oi;
            return answered ? (
              <div key={oi} className="relative rounded-lg overflow-hidden px-3 py-2 text-sm" style={{ background: "var(--bg-surface)", border: mine ? "1px solid var(--accent)" : "1px solid var(--border)" }} data-testid={`${tid}-opt-${oi}`}>
                <div className="absolute inset-y-0 left-0" style={{ width: `${pct}%`, background: "color-mix(in srgb, var(--accent) 22%, transparent)" }} />
                <div className="relative flex justify-between"><span>{o}{mine && " ✓"}</span><span style={{ color: "var(--text-muted)" }}>{pct}%</span></div>
              </div>
            ) : (
              <button key={oi} onClick={() => setDraft(oi)} data-testid={`${tid}-opt-${oi}`}
                className="w-full text-left rounded-lg px-3 py-2 text-sm" style={draft === oi ? { background: "var(--accent)", color: "#fff" } : { background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }}>{o}</button>
            );
          })}
        </div>
      </div>
    );
  }

  if (q.type === "ranking") {
    const order = draft || q.options.map((_, i) => i);
    const move = (idx, dir) => {
      const ni = idx + dir; if (ni < 0 || ni >= order.length) return;
      const no = [...order]; [no[idx], no[ni]] = [no[ni], no[idx]]; setDraft(no);
    };
    return (
      <div data-testid={tid}>
        <div className="text-sm font-medium mb-1.5">{q.text} <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>(rank in order)</span></div>
        <div className="space-y-1.5">
          {(answered ? myAnswer : order).map((optIdx, pos) => (
            <div key={optIdx} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)" }} data-testid={`${tid}-rank-${pos}`}>
              <span className="gold-text font-semibold w-4">{pos + 1}</span>
              <span className="flex-1">{q.options[optIdx]}</span>
              {!answered && (
                <span className="flex gap-1">
                  <button onClick={() => move(pos, -1)} data-testid={`${tid}-up-${pos}`}><ArrowUp size={14} style={{ color: "var(--text-muted)" }} /></button>
                  <button onClick={() => move(pos, 1)} data-testid={`${tid}-down-${pos}`}><ArrowDown size={14} style={{ color: "var(--text-muted)" }} /></button>
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (q.type === "sum") {
    const map = draft || {};
    const total = Object.values(map).reduce((a, b) => a + (Number(b) || 0), 0);
    return (
      <div data-testid={tid}>
        <div className="text-sm font-medium mb-1.5">{q.text} <span className="text-[10px]" style={{ color: total === 100 ? "var(--accent)" : "var(--text-muted)" }}>Place points based on priority. {total}/100</span></div>
        <div className="space-y-1.5">
          {q.options.map((o, oi) => (
            <div key={oi} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)" }}>
              <span className="flex-1">{o}</span>
              {answered ? <span className="gold-text font-semibold">{(myAnswer || {})[oi] || 0} pts</span>
                : <input type="number" min={0} max={100} value={map[oi] ?? ""} onChange={(e) => {
                    const others = Object.entries(map).reduce((a, [k, v]) => a + (k !== String(oi) ? (Number(v) || 0) : 0), 0);
                    let v = Number(e.target.value) || 0;
                    if (v < 0) v = 0;
                    const maxAllowed = 100 - others;
                    if (v > maxAllowed) v = maxAllowed;
                    setDraft({ ...map, [oi]: v });
                  }}
                    data-testid={`${tid}-pts-${oi}`} className="w-16 rounded-lg px-2 py-1 text-sm text-right outline-none" style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />}
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

function ResultsView({ results }) {
  if (!results) return <div className="grid place-items-center py-10"><Loader2 className="animate-spin gold-text" /></div>;
  return (
    <div className="space-y-6" data-testid="poll-results-view">
      <div className="text-sm" style={{ color: "var(--text-muted)" }}>{results.total_respondents} respondent{results.total_respondents === 1 ? "" : "s"}</div>
      {results.questions.map((q, qi) => (
        <div key={q.id} data-testid={`poll-result-q${qi}`}>
          <div className="font-serif-display text-lg mb-2">{q.text}</div>
          <QuestionChart q={q} qi={qi} />
          {q.voters.length === 0 ? <div className="text-xs" style={{ color: "var(--text-muted)" }}>No responses yet.</div> : (
            <div className="space-y-1.5 mt-3">
              {q.voters.map((v, vi) => (
                <div key={vi} className="flex items-start justify-between gap-3 rounded-lg px-3 py-2 text-sm" style={{ background: "var(--overlay)" }} data-testid={`poll-result-q${qi}-voter-${vi}`}>
                  <span className="font-medium">{v.name}</span>
                  <span className="text-right" style={{ color: "var(--text-secondary)" }}>{formatAnswer(q, v.answer)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Aggregate chart per question (skips short-answer, which has no numeric aggregate).
function QuestionChart({ q, qi }) {
  if (q.type === "short" || !q.voters?.length) return null;
  let data = [];
  let label = "";
  if (q.type === "mc") {
    label = "Votes per option";
    data = q.options.map((o, oi) => ({ name: o, value: q.voters.filter((v) => v.answer === oi).length }));
  } else if (q.type === "sum") {
    label = "Average points (of 100)";
    data = q.options.map((o, oi) => {
      const vals = q.voters.map((v) => Number((v.answer || {})[oi]) || 0);
      const avg = vals.reduce((a, b) => a + b, 0) / (vals.length || 1);
      return { name: o, value: Math.round(avg * 10) / 10 };
    });
  } else if (q.type === "ranking") {
    label = "Average rank (lower is better)";
    data = q.options.map((o, oi) => {
      const positions = q.voters.map((v) => {
        const arr = v.answer || [];
        const idx = arr.indexOf(oi);
        return idx < 0 ? arr.length : idx + 1;
      });
      const avg = positions.reduce((a, b) => a + b, 0) / (positions.length || 1);
      return { name: o, value: Math.round(avg * 100) / 100 };
    });
  } else return null;

  const trunc = (s) => (s && s.length > 10 ? s.slice(0, 9) + "…" : s);
  return (
    <div className="rounded-xl p-3 mb-1" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }} data-testid={`poll-chart-q${qi}`}>
      <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>{label}</div>
      <ResponsiveContainer width="100%" height={Math.max(140, data.length * 34)}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 0 }}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" width={78} tick={{ fontSize: 11, fill: "var(--text-secondary)" }} tickFormatter={trunc} axisLine={false} tickLine={false} />
          <Tooltip cursor={{ fill: "transparent" }} contentStyle={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 10, fontSize: 12 }}
            labelStyle={{ color: "var(--text-primary)" }} itemStyle={{ color: "var(--accent)" }} />
          <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={18}>
            {data.map((_, i) => <Cell key={i} fill="var(--accent)" />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function formatAnswer(q, ans) {
  if (q.type === "mc") return q.options[ans];
  if (q.type === "short") return ans;
  if (q.type === "ranking") return (ans || []).map((i, pos) => `${pos + 1}. ${q.options[i]}`).join("  ");
  if (q.type === "sum") return Object.entries(ans || {}).map(([oi, p]) => `${q.options[oi]}: ${p}`).join(", ");
  return JSON.stringify(ans);
}
